import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { useSelector } from 'react-redux';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2';
import { 
    CircularProgress, 
    IconButton, 
    MenuItem, 
    Select, 
    FormControl,
    Tooltip,
    Button,
    InputLabel
} from '@mui/material';
import { 
    ChevronLeft, 
    ChevronRight,
    TrendingUp,
    AccountBalanceWallet,
    Business,
    Refresh,
    ShowChart,
    Close,
    Event,
    Subscriptions,
    Receipt,
    MenuBook,
    ExpandMore,
    LocalOffer,
    ShoppingCart,
    AccountBalance
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

// Types
interface LedgerSummary {
    month?: string;
    year?: string;
    totalPaid: number;
    totalPending: number;
    totalSettled: number;
    totalWriteoff: number;
    platformFees: number;
    totalLedgerEntries: number;
    totalPayout: number;
    event?: {
        totalPaid?: number;
        totalPending?: number;
    };
    subscription?: {
        totalPaid?: number;
        totalPending?: number;
    };
    byType?: {
        [key: string]: {
            totalPaid: number;
            totalPending: number;
            totalSettled: number;
            totalWriteoff: number;
            platformFees: number;
            count?: {
                paid: number;
                due: number;
                settled: number;
                transactions: number;
            };
        };
    };
}

interface LedgerEntry {
    id: string;
    action: 'RECEIVABLE' | 'ADJUSTMENT' | 'RECEIVED' | 'CANCELLED' | 'SETTLED';
    amount: number;
    createdAt: any;
    currency: string;
    customerId?: string;
    customerName?: string;
    orderId?: string;
    subscriptionId?: string;
    subscriptionName?: string;
    paymentStatus: 'PAID' | 'PENDING' | 'CANCELLED';
    paymentMode?: string;
    platformFees?: number;
    transferAmount?: number;
    type: 'subscription' | 'product' | 'event' | 'offer' | 'payout';
    transactionTime?: any;
    settlementTime?: any;
}

interface SuperAdminAccount {
    id: string;
    approverSPID: string;
    approverBusinessName: string;
    approverPhoneNumber: string;
    status: string;
    createdAt?: any;
}

interface AccountLedgerData {
    account: SuperAdminAccount;
    summary: LedgerSummary | null;
    loading: boolean;
    error: string | null;
}

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const CHART_COLORS = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', 
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
];

const ENTRIES_PER_PAGE = 100;

function getCurrentMonthName(): string {
    const now = new Date();
    return MONTH_NAMES[now.getMonth()];
}

function computeLedgerFY(selectedYear: number, monthName: string): string {
    const monthIndex = MONTH_NAMES.indexOf(monthName);
    const fy = monthIndex >= 3 ? selectedYear : selectedYear - 1;
    return `FY${fy}`;
}

const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount);
};

const formatCompact = (amount: number) => {
    if (amount >= 10000000) return `${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(1)}K`;
    return `${amount}`;
};

const getTypeIcon = (type: string) => {
    const iconMap: Record<string, JSX.Element> = {
        subscription: <Subscriptions className="text-purple-500" fontSize="small" />,
        event: <Event className="text-blue-500" fontSize="small" />,
        offer: <LocalOffer className="text-green-500" fontSize="small" />,
        product: <ShoppingCart className="text-orange-500" fontSize="small" />,
        payout: <AccountBalance className="text-red-500" fontSize="small" />,
    };
    return iconMap[type?.toLowerCase()] || <Receipt className="text-gray-500" fontSize="small" />;
};

const getStatusStyles = (status: string) => {
    switch (status) {
        case 'PAID':
            return { bg: 'bg-emerald-50', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800', label: 'Paid' };
        case 'PENDING':
            return { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', label: 'UnPaid' };
        case 'CANCELLED':
            return { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-800', label: 'Written Off' };
        default:
            return { bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-800', label: status };
    }
};

const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (dateOnly.getTime() === today.getTime()) {
        return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function SuperAdminDashboard() {
    const serviceProviderId = useSelector(selectEffectiveUID);
    
    const [loading, setLoading] = useState(true);
    const [accountsData, setAccountsData] = useState<AccountLedgerData[]>([]);
    const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonthName());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [showTrendsModal, setShowTrendsModal] = useState(false);
    const [selectedAccountForTrends, setSelectedAccountForTrends] = useState<AccountLedgerData | null>(null);
    const [historicalData, setHistoricalData] = useState<LedgerSummary[]>([]);
    const [loadingHistorical, setLoadingHistorical] = useState(false);
    
    // Ledger Modal States
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [ledgerEntries, setLedgerEntries] = useState<LedgerEntry[]>([]);
    const [ledgerMonthlyStats, setLedgerMonthlyStats] = useState<LedgerSummary | null>(null);
    const [loadingLedger, setLoadingLedger] = useState(false);
    const [ledgerYear, setLedgerYear] = useState<string>('');
    const [ledgerMonth, setLedgerMonth] = useState<string>('');
    const [availableLedgerYears, setAvailableLedgerYears] = useState<string[]>([]);
    const [availableLedgerMonths, setAvailableLedgerMonths] = useState<string[]>([]);
    const [ledgerFilterType, setLedgerFilterType] = useState<'all' | 'PAID' | 'PENDING' | 'CANCELLED'>('all');
    const [hasMoreEntries, setHasMoreEntries] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const lastVisibleRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);

    const fetchSuperAdminAccounts = useCallback(async () => {
        if (!serviceProviderId) return;
        
        setLoading(true);
        try {
            const requestsQuery = query(
                collection(db, 'SuperAdminRequests'),
                where('initiatorSPID', '==', serviceProviderId),
                where('status', '==', 'approved')
            );
            
            const requestsSnapshot = await getDocs(requestsQuery);
            const accounts: SuperAdminAccount[] = requestsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as SuperAdminAccount[];

            const initialAccountsData: AccountLedgerData[] = accounts.map(account => ({
                account,
                summary: null,
                loading: true,
                error: null
            }));

            setAccountsData(initialAccountsData);
            setLoading(false);

            await Promise.all(accounts.map(async (account, index) => {
                try {
                    const ledgerFY = computeLedgerFY(selectedYear, selectedMonth);
                    const ledgerDocRef = doc(
                        db,
                        'ServiceProviders',
                        account.approverSPID,
                        'Ledger',
                        ledgerFY,
                        'months',
                        selectedMonth
                    );
                    
                    const ledgerDoc = await getDoc(ledgerDocRef);
                    const summary = ledgerDoc.exists() ? (ledgerDoc.data() as LedgerSummary) : null;

                    setAccountsData(prev => {
                        const updated = [...prev];
                        updated[index] = { ...updated[index], summary, loading: false, error: null };
                        return updated;
                    });
                } catch (error: any) {
                    setAccountsData(prev => {
                        const updated = [...prev];
                        updated[index] = { ...updated[index], loading: false, error: error?.message || 'Failed' };
                        return updated;
                    });
                }
            }));
        } catch (error) {
            console.error('Error fetching super admin accounts:', error);
            setLoading(false);
        }
    }, [serviceProviderId, selectedYear, selectedMonth]);

    const loadHistoricalData = useCallback(async (account: SuperAdminAccount) => {
        setLoadingHistorical(true);
        try {
            const allHistoricalData: LedgerSummary[] = [];
            const ledgerRef = collection(db, `ServiceProviders/${account.approverSPID}/Ledger`);
            const yearsSnapshot = await getDocs(ledgerRef);
            
            for (const yearDoc of yearsSnapshot.docs) {
                const year = yearDoc.id;
                const monthsRef = collection(db, `ServiceProviders/${account.approverSPID}/Ledger/${year}/months`);
                const monthsSnapshot = await getDocs(monthsRef);
                
                for (const monthDoc of monthsSnapshot.docs) {
                    const monthName = monthDoc.id;
                    const monthData = monthDoc.data() as LedgerSummary;
                    allHistoricalData.push({ ...monthData, month: monthName, year });
                }
            }
            
            allHistoricalData.sort((a, b) => {
                const yearA = a.year || '';
                const yearB = b.year || '';
                if (yearA !== yearB) return yearA.localeCompare(yearB);
                return MONTH_NAMES.indexOf(a.month || '') - MONTH_NAMES.indexOf(b.month || '');
            });
            
            setHistoricalData(allHistoricalData);
        } catch (error) {
            console.error('Error loading historical data:', error);
        } finally {
            setLoadingHistorical(false);
        }
    }, []);

    // Load available years for ledger
    const loadLedgerYears = useCallback(async (spid: string) => {
        try {
            const ledgerRef = collection(db, `ServiceProviders/${spid}/Ledger`);
            const snapshot = await getDocs(ledgerRef);
            const years = snapshot.docs.map(d => d.id).sort().reverse();
            setAvailableLedgerYears(years);
            if (years.length > 0) {
                setLedgerYear(years[0]);
            }
        } catch (error) {
            console.error('Error loading ledger years:', error);
        }
    }, []);

    // Load available months for selected year
    const loadLedgerMonths = useCallback(async (spid: string, year: string) => {
        try {
            const monthsRef = collection(db, `ServiceProviders/${spid}/Ledger/${year}/months`);
            const snapshot = await getDocs(monthsRef);
            const months = snapshot.docs.map(d => d.id).sort((a, b) => MONTH_NAMES.indexOf(b) - MONTH_NAMES.indexOf(a));
            setAvailableLedgerMonths(months);
            if (months.length > 0) {
                const currentMonth = MONTH_NAMES[new Date().getMonth()];
                setLedgerMonth(months.includes(currentMonth) ? currentMonth : months[0]);
            }
        } catch (error) {
            console.error('Error loading ledger months:', error);
        }
    }, []);

    // Load ledger entries
    const loadLedgerEntries = useCallback(async (spid: string, year: string, month: string, reset = true) => {
        if (!spid || !year || !month) return;
        
        if (reset) {
            setLoadingLedger(true);
            setLedgerEntries([]);
            lastVisibleRef.current = null;
            setHasMoreEntries(false);
        } else {
            setLoadingMore(true);
        }

        try {
            // Load monthly stats only on reset
            if (reset) {
                const monthDocRef = doc(db, `ServiceProviders/${spid}/Ledger/${year}/months/${month}`);
                const monthDocSnap = await getDoc(monthDocRef);
                if (monthDocSnap.exists()) {
                    setLedgerMonthlyStats(monthDocSnap.data() as LedgerSummary);
                } else {
                    setLedgerMonthlyStats(null);
                }
            }

            // Load entries
            const entriesRef = collection(db, `ServiceProviders/${spid}/Ledger/${year}/months/${month}/entries`);
            let q = query(entriesRef, orderBy('createdAt', 'desc'), limit(ENTRIES_PER_PAGE));
            
            if (!reset && lastVisibleRef.current) {
                q = query(entriesRef, orderBy('createdAt', 'desc'), startAfter(lastVisibleRef.current), limit(ENTRIES_PER_PAGE));
            }

            const snapshot = await getDocs(q);
            const entries = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as LedgerEntry[];
            
            if (reset) {
                setLedgerEntries(entries);
            } else {
                setLedgerEntries(prev => [...prev, ...entries]);
            }

            if (snapshot.docs.length === ENTRIES_PER_PAGE) {
                lastVisibleRef.current = snapshot.docs[snapshot.docs.length - 1];
                setHasMoreEntries(true);
            } else {
                lastVisibleRef.current = null;
                setHasMoreEntries(false);
            }
        } catch (error) {
            console.error('Error loading ledger entries:', error);
        } finally {
            setLoadingLedger(false);
            setLoadingMore(false);
        }
    }, []);

    // Open ledger modal
    const handleViewLedger = useCallback(async () => {
        if (!selectedAccountForTrends) return;
        
        // Reset ledger state
        setLedgerEntries([]);
        setLedgerMonthlyStats(null);
        lastVisibleRef.current = null;
        setHasMoreEntries(false);
        setLedgerFilterType('all');
        setLoadingLedger(false);
        setLoadingMore(false);
        
        setShowLedgerModal(true);
        await loadLedgerYears(selectedAccountForTrends.account.approverSPID);
    }, [selectedAccountForTrends, loadLedgerYears]);

    // Load months when year changes
    useEffect(() => {
        if (showLedgerModal && selectedAccountForTrends?.account?.approverSPID && ledgerYear) {
            loadLedgerMonths(selectedAccountForTrends.account.approverSPID, ledgerYear);
        }
    }, [ledgerYear, showLedgerModal, selectedAccountForTrends?.account?.approverSPID, loadLedgerMonths]);

    // Load entries when month changes
    useEffect(() => {
        if (showLedgerModal && selectedAccountForTrends?.account?.approverSPID && ledgerYear && ledgerMonth) {
            loadLedgerEntries(selectedAccountForTrends.account.approverSPID, ledgerYear, ledgerMonth, true);
        }
    }, [ledgerMonth, ledgerYear, showLedgerModal, selectedAccountForTrends?.account?.approverSPID, loadLedgerEntries]);

    const handleShowTrends = useCallback(async (accountData: AccountLedgerData) => {
        setSelectedAccountForTrends(accountData);
        setShowTrendsModal(true);
        await loadHistoricalData(accountData.account);
    }, [loadHistoricalData]);

    useEffect(() => {
        fetchSuperAdminAccounts();
    }, [fetchSuperAdminAccounts]);

    const navigateMonth = (direction: 'prev' | 'next') => {
        const currentIndex = MONTH_NAMES.indexOf(selectedMonth);
        if (direction === 'prev') {
            if (currentIndex === 0) {
                setSelectedMonth(MONTH_NAMES[11]);
                setSelectedYear(prev => prev - 1);
            } else {
                setSelectedMonth(MONTH_NAMES[currentIndex - 1]);
            }
        } else {
            if (currentIndex === 11) {
                setSelectedMonth(MONTH_NAMES[0]);
                setSelectedYear(prev => prev + 1);
            } else {
                setSelectedMonth(MONTH_NAMES[currentIndex + 1]);
            }
        }
    };

    const aggregateMetrics = useMemo(() => {
        let totalRevenue = 0, totalPending = 0, totalSettled = 0, totalPlatformFees = 0;
        let eventRevenue = 0, subscriptionRevenue = 0, activeAccounts = 0;

        accountsData.forEach(data => {
            if (data.summary) {
                totalRevenue += data.summary.totalPaid || 0;
                totalPending += data.summary.totalPending || 0;
                totalSettled += data.summary.totalSettled || 0;
                totalPlatformFees += data.summary.platformFees || 0;
                eventRevenue += data.summary.event?.totalPaid || 0;
                subscriptionRevenue += data.summary.subscription?.totalPaid || 0;
                if ((data.summary.totalPaid || 0) > 0) activeAccounts++;
            }
        });

        return { totalRevenue, totalPending, totalSettled, totalPlatformFees, eventRevenue, subscriptionRevenue, activeAccounts, totalAccounts: accountsData.length };
    }, [accountsData]);

    const pieChartData = useMemo(() => {
        return accountsData
            .filter(d => d.summary && (d.summary.totalPaid || 0) > 0)
            .map((d, index) => ({
                name: d.account.approverBusinessName || 'Unknown',
                value: d.summary?.totalPaid || 0,
                color: CHART_COLORS[index % CHART_COLORS.length]
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [accountsData]);

    // Filtered ledger entries
    const filteredLedgerEntries = useMemo(() => {
        if (ledgerFilterType === 'all') return ledgerEntries;
        return ledgerEntries.filter(e => e.paymentStatus === ledgerFilterType);
    }, [ledgerEntries, ledgerFilterType]);

    if (loading) {
        return (
            <div className="min-h-[400px] flex items-center justify-center">
                <CircularProgress size={40} sx={{ color: '#6366f1' }} />
            </div>
        );
    }

    if (accountsData.length === 0) {
        return (
            <div className="min-h-[400px] flex items-center justify-center p-6">
                <div className="text-center max-w-sm">
                    <div className="w-16 h-16 bg-gradient-to-br from-violet-100 to-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                        <Business className="text-violet-500" sx={{ fontSize: 32 }} />
                    </div>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">No Academies Connected</h2>
                    <p className="text-sm text-slate-600 mb-5">
                        You don't have access to any academies yet. Request access from other academies to view their data.
                    </p>
                    <Button
                        variant="contained"
                        onClick={() => {
                            window.location.href = '/team-management?tab=3';
                        }}
                        sx={{ 
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: '0.875rem',
                            px: 3,
                            py: 1
                        }}
                    >
                        Request Access from Other Academies
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gradient-to-br from-slate-50 via-violet-50/30 to-slate-50 min-h-screen">
            <div className="max-w-7xl mx-auto px-4 py-5 sm:px-6">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 bg-clip-text text-transparent">
                            Manage Your Academies
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">{aggregateMetrics.activeAccounts} of {aggregateMetrics.totalAccounts} accounts active</p>
                    </div>
                    <Button
                        variant="contained"
                        onClick={() => {
                            window.location.href = '/team-management?tab=3';
                        }}
                        sx={{ 
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontSize: '0.875rem',
                            px: 3,
                            py: 1,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        Request Access from Other Academies
                    </Button>
                    
                    {/* Month Selector */}
                    <div className="flex items-center gap-1.5 bg-white rounded-xl shadow-sm border border-slate-200/80 px-2 py-1">
                        <IconButton size="small" onClick={() => navigateMonth('prev')} sx={{ color: '#6366f1' }}>
                            <ChevronLeft />
                        </IconButton>
                        
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600, fontSize: '0.875rem', color: '#334155' }}
                            >
                                {MONTH_NAMES.map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.875rem' }}>{m}</MenuItem>)}
                            </Select>
                        </FormControl>
                        
                        <FormControl size="small" sx={{ minWidth: 70 }}>
                            <Select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(Number(e.target.value))}
                                sx={{ '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, fontWeight: 600, fontSize: '0.875rem', color: '#334155' }}
                            >
                                {[-1, 0, 1].map(offset => {
                                    const yr = new Date().getFullYear() + offset;
                                    return <MenuItem key={yr} value={yr} sx={{ fontSize: '0.875rem' }}>{yr}</MenuItem>;
                                })}
                            </Select>
                        </FormControl>
                        
                        <IconButton size="small" onClick={() => navigateMonth('next')} sx={{ color: '#6366f1' }}>
                            <ChevronRight />
                        </IconButton>
                        <IconButton size="small" onClick={fetchSuperAdminAccounts} sx={{ color: '#6366f1' }}>
                            <Refresh />
                        </IconButton>
                    </div>
                </div>

                {/* Aggregate Stats Row */}
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5">
                    <Stat icon={<TrendingUp />} label="Revenue" value={aggregateMetrics.totalRevenue} color="emerald" />
                    <Stat icon={<AccountBalanceWallet />} label="Pending" value={aggregateMetrics.totalPending} color="amber" />
                    <Stat icon={<Receipt />} label="Settled" value={aggregateMetrics.totalSettled} color="violet" />
                    <Stat icon={<Business />} label="Fees" value={aggregateMetrics.totalPlatformFees} color="slate" className="hidden sm:block" />
                    <Stat icon={<Event />} label="Events" value={aggregateMetrics.eventRevenue} color="blue" className="hidden lg:block" />
                    <Stat icon={<Subscriptions />} label="Subs" value={aggregateMetrics.subscriptionRevenue} color="purple" className="hidden lg:block" />
                </div>

                {/* Main Content Grid */}
                <div className="grid lg:grid-cols-3 gap-5">
                    {/* Pie Chart */}
                    {pieChartData.length > 0 && (
                        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm border border-slate-200/60 p-4">
                            <h3 className="text-sm font-semibold text-slate-700 mb-3">Revenue Distribution</h3>
                            <div style={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={pieChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
                                            {pieChartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                        </Pie>
                                        <RechartsTooltip formatter={(v: number) => formatCurrency(v)} contentStyle={{ fontSize: '12px', borderRadius: '10px', padding: '8px 12px' }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mt-3">
                                {pieChartData.map((entry, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: entry.color }} />
                                        <span className="truncate text-slate-600 flex-1">{entry.name}</span>
                                        <span className="text-slate-500 font-medium">₹{formatCompact(entry.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Accounts Table */}
                    <div className={`${pieChartData.length > 0 ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden`}>
                        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-slate-700">Managed Accounts</h3>
                            <span className="text-sm text-slate-400">{accountsData.length} accounts</span>
                        </div>
                        
                        {/* Desktop Table */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50/80">
                                    <tr>
                                        <th className="text-left py-3 px-4 font-medium text-slate-500">Academy</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Revenue</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Pending</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Settled</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Events</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Subs</th>
                                        <th className="text-right py-3 px-3 font-medium text-slate-500">Fees</th>
                                        <th className="text-center py-3 px-3 font-medium text-slate-500"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountsData
                                        .sort((a, b) => (b.summary?.totalPaid || 0) - (a.summary?.totalPaid || 0))
                                        .map((data, i) => (
                                        <tr key={data.account.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-3">
                                                    <div 
                                                        className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                                                        style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                                                    >
                                                        {(data.account.approverBusinessName || 'A').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-medium text-slate-800 truncate max-w-[160px]">{data.account.approverBusinessName || 'Unknown'}</p>
                                                        <p className="text-slate-400 text-xs">{data.account.approverPhoneNumber}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            {data.loading ? (
                                                <td colSpan={7} className="text-center py-3">
                                                    <CircularProgress size={18} sx={{ color: '#6366f1' }} />
                                                </td>
                                            ) : data.error ? (
                                                <td colSpan={7} className="text-center py-3 text-red-500 text-sm">Error loading</td>
                                            ) : (
                                                <>
                                                    <td className="text-right py-3 px-3 font-semibold text-emerald-600">₹{formatCompact(data.summary?.totalPaid || 0)}</td>
                                                    <td className="text-right py-3 px-3 text-amber-600">₹{formatCompact(data.summary?.totalPending || 0)}</td>
                                                    <td className="text-right py-3 px-3 text-violet-600">₹{formatCompact(data.summary?.totalSettled || 0)}</td>
                                                    <td className="text-right py-3 px-3 text-blue-600">₹{formatCompact(data.summary?.event?.totalPaid || 0)}</td>
                                                    <td className="text-right py-3 px-3 text-purple-600">₹{formatCompact(data.summary?.subscription?.totalPaid || 0)}</td>
                                                    <td className="text-right py-3 px-3 text-slate-500">₹{formatCompact(data.summary?.platformFees || 0)}</td>
                                                    <td className="text-center py-3 px-3">
                                                        <Tooltip title="View Trends">
                                                            <IconButton size="small" onClick={() => handleShowTrends(data)}>
                                                                <ShowChart sx={{ color: '#6366f1', fontSize: 20 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        <div className="sm:hidden divide-y divide-slate-100">
                            {accountsData
                                .sort((a, b) => (b.summary?.totalPaid || 0) - (a.summary?.totalPaid || 0))
                                .map((data, i) => (
                                <div key={data.account.id} className="p-4">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div 
                                            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-semibold"
                                            style={{ background: CHART_COLORS[i % CHART_COLORS.length] }}
                                        >
                                            {(data.account.approverBusinessName || 'A').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-slate-800 truncate">{data.account.approverBusinessName || 'Unknown'}</p>
                                            <p className="text-slate-400 text-xs">{data.account.approverPhoneNumber}</p>
                                        </div>
                                        <IconButton size="small" onClick={() => handleShowTrends(data)}>
                                            <ShowChart sx={{ color: '#6366f1', fontSize: 20 }} />
                                        </IconButton>
                                    </div>
                                    {data.loading ? (
                                        <div className="flex justify-center py-3"><CircularProgress size={20} sx={{ color: '#6366f1' }} /></div>
                                    ) : data.error ? (
                                        <p className="text-red-500 text-sm text-center">Error loading data</p>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-2">
                                            <MetricPill label="Revenue" value={data.summary?.totalPaid || 0} color="emerald" />
                                            <MetricPill label="Pending" value={data.summary?.totalPending || 0} color="amber" />
                                            <MetricPill label="Settled" value={data.summary?.totalSettled || 0} color="violet" />
                                            <MetricPill label="Events" value={data.summary?.event?.totalPaid || 0} color="blue" />
                                            <MetricPill label="Subs" value={data.summary?.subscription?.totalPaid || 0} color="purple" />
                                            <MetricPill label="Fees" value={data.summary?.platformFees || 0} color="slate" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Trends Modal */}
            {showTrendsModal && selectedAccountForTrends && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-slate-800">Historical Trends</h2>
                                <p className="text-sm text-slate-500">{selectedAccountForTrends.account.approverBusinessName}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<MenuBook />}
                                    onClick={handleViewLedger}
                                    sx={{ 
                                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    View Ledger
                                </Button>
                                <IconButton onClick={() => setShowTrendsModal(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                        </div>

                        <div className="p-5 overflow-y-auto max-h-[calc(90vh-72px)]">
                            {loadingHistorical ? (
                                <div className="flex items-center justify-center py-16">
                                    <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                                </div>
                            ) : historicalData.length === 0 ? (
                                <div className="text-center py-16">
                                    <ShowChart className="text-slate-300 mx-auto mb-4" sx={{ fontSize: 56 }} />
                                    <p className="text-slate-500">No historical data available</p>
                                </div>
                            ) : (
                                <div className="space-y-5">
                                    {/* Charts */}
                                    <div className="grid md:grid-cols-2 gap-5">
                                        <div className="bg-emerald-50/50 rounded-xl p-4 border border-emerald-100">
                                            <h3 className="text-sm font-semibold text-slate-700 mb-3">Revenue & Pending</h3>
                                            <ResponsiveContainer width="100%" height={220}>
                                                <BarChart data={historicalData.slice(-8)}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis dataKey="month" stroke="#6b7280" fontSize={11} tickFormatter={(v) => v?.substring(0, 3) || ''} />
                                                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                                    <RechartsTooltip contentStyle={{ fontSize: '12px', borderRadius: '10px' }} formatter={(v: number) => formatCurrency(v)} />
                                                    <Bar dataKey="totalPaid" fill="#10b981" name="Paid" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="totalPending" fill="#f59e0b" name="Pending" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                        <div className="bg-violet-50/50 rounded-xl p-4 border border-violet-100">
                                            <h3 className="text-sm font-semibold text-slate-700 mb-3">Settlements & Payout</h3>
                                            <ResponsiveContainer width="100%" height={220}>
                                                <BarChart data={historicalData.slice(-8)}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                                    <XAxis dataKey="month" stroke="#6b7280" fontSize={11} tickFormatter={(v) => v?.substring(0, 3) || ''} />
                                                    <YAxis stroke="#6b7280" fontSize={11} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                                                    <RechartsTooltip contentStyle={{ fontSize: '12px', borderRadius: '10px' }} formatter={(v: number) => formatCurrency(v)} />
                                                    <Bar dataKey="totalSettled" fill="#6366f1" name="Settled" radius={[4, 4, 0, 0]} />
                                                    <Bar dataKey="totalPayout" fill="#8b5cf6" name="Payout" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Table */}
                                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                                        <h3 className="text-sm font-semibold text-slate-700 px-4 py-3 bg-slate-50 border-b border-slate-200">Monthly Summary</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead className="bg-slate-50/50">
                                                    <tr>
                                                        <th className="text-left py-2.5 px-4 font-medium text-slate-500">Month</th>
                                                        <th className="text-right py-2.5 px-3 font-medium text-slate-500">Paid</th>
                                                        <th className="text-right py-2.5 px-3 font-medium text-slate-500">Pending</th>
                                                        <th className="text-right py-2.5 px-3 font-medium text-slate-500">Settled</th>
                                                        <th className="text-right py-2.5 px-3 font-medium text-slate-500">Txns</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {historicalData.slice(-8).reverse().map((d, i) => (
                                                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                                            <td className="py-2.5 px-4 text-slate-700 font-medium">{d.month?.substring(0, 3)} {d.year?.replace('FY', '')}</td>
                                                            <td className="py-2.5 px-3 text-right text-emerald-600 font-medium">₹{formatCompact(d.totalPaid)}</td>
                                                            <td className="py-2.5 px-3 text-right text-amber-600">₹{formatCompact(d.totalPending)}</td>
                                                            <td className="py-2.5 px-3 text-right text-violet-600">₹{formatCompact(d.totalSettled)}</td>
                                                            <td className="py-2.5 px-3 text-right text-slate-500">{d.totalLedgerEntries || 0}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Ledger Modal */}
            {showLedgerModal && selectedAccountForTrends && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-2 sm:p-4">
                    <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
                        {/* Header */}
                        <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                    <MenuBook className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-800">Ledger Book</h2>
                                    <p className="text-sm text-slate-500">{selectedAccountForTrends.account.approverBusinessName}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Year Selector */}
                                <FormControl size="small" sx={{ minWidth: 90 }}>
                                    <InputLabel>Year</InputLabel>
                                    <Select
                                        value={ledgerYear}
                                        label="Year"
                                        onChange={(e) => setLedgerYear(e.target.value)}
                                        sx={{ fontSize: '0.8rem' }}
                                    >
                                        {availableLedgerYears.map(y => <MenuItem key={y} value={y} sx={{ fontSize: '0.8rem' }}>{y}</MenuItem>)}
                                    </Select>
                                </FormControl>
                                
                                {/* Month Navigation */}
                                <div className="flex items-center gap-1 bg-slate-100 rounded-lg px-1 py-0.5">
                                    <IconButton 
                                        size="small"
                                        disabled={availableLedgerMonths.indexOf(ledgerMonth) === 0}
                                        onClick={() => {
                                            const idx = availableLedgerMonths.indexOf(ledgerMonth);
                                            if (idx > 0) setLedgerMonth(availableLedgerMonths[idx - 1]);
                                        }}
                                    >
                                        <ChevronLeft fontSize="small" />
                                    </IconButton>
                                    <span className="text-sm font-semibold text-slate-700 min-w-[70px] text-center">{ledgerMonth?.substring(0, 3)}</span>
                                    <IconButton 
                                        size="small"
                                        disabled={availableLedgerMonths.indexOf(ledgerMonth) === availableLedgerMonths.length - 1}
                                        onClick={() => {
                                            const idx = availableLedgerMonths.indexOf(ledgerMonth);
                                            if (idx < availableLedgerMonths.length - 1) setLedgerMonth(availableLedgerMonths[idx + 1]);
                                        }}
                                    >
                                        <ChevronRight fontSize="small" />
                                    </IconButton>
                                </div>
                                
                                <IconButton onClick={() => {
                                    setShowLedgerModal(false);
                                    // Reset state when closing
                                    setLedgerEntries([]);
                                    setLedgerMonthlyStats(null);
                                    lastVisibleRef.current = null;
                                    setHasMoreEntries(false);
                                }}>
                                    <Close />
                                </IconButton>
                            </div>
                        </div>

                        {/* Summary Cards */}
                        {ledgerMonthlyStats && (
                            <div className="bg-white border-b border-slate-100 px-4 sm:px-6 py-3 flex-shrink-0">
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="text-center">
                                        <p className="text-xs text-slate-500 mb-0.5">Paid</p>
                                        <p className="text-lg font-bold text-emerald-600">{formatCurrency(ledgerMonthlyStats.totalPaid || 0)}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-slate-500 mb-0.5">UnPaid</p>
                                        <p className="text-lg font-bold text-amber-600">{formatCurrency(ledgerMonthlyStats.totalPending || 0)}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-slate-500 mb-0.5">Settled</p>
                                        <p className="text-lg font-bold text-violet-600">{formatCurrency(ledgerMonthlyStats.totalSettled || 0)}</p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-xs text-slate-500 mb-0.5">Gateway Fees</p>
                                        <p className="text-lg font-bold text-slate-600">{formatCurrency(ledgerMonthlyStats.platformFees || 0)}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Filter Buttons */}
                        <div className="bg-white px-4 sm:px-6 py-3 border-b border-slate-100 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <div className="flex gap-2 flex-wrap">
                                    {(['all', 'PAID', 'PENDING', 'CANCELLED'] as const).map(filter => (
                                        <button
                                            key={filter}
                                            onClick={() => setLedgerFilterType(filter)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                                ledgerFilterType === filter
                                                    ? filter === 'all' ? 'bg-slate-800 text-white'
                                                    : filter === 'PAID' ? 'bg-emerald-100 text-emerald-700'
                                                    : filter === 'PENDING' ? 'bg-amber-100 text-amber-700'
                                                    : 'bg-red-100 text-red-700'
                                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                            }`}
                                        >
                                            {filter === 'all' ? 'All' : filter === 'PAID' ? '✓ Paid' : filter === 'PENDING' ? '⏱ UnPaid' : '✗ Written Off'}
                                        </button>
                                    ))}
                                </div>
                                <span className="text-xs text-slate-500">{filteredLedgerEntries.length} entries</span>
                            </div>
                        </div>

                        {/* Entries List */}
                        <div className="flex-1 overflow-y-auto">
                            {loadingLedger ? (
                                <div className="flex items-center justify-center py-20">
                                    <CircularProgress size={40} sx={{ color: '#6366f1' }} />
                                </div>
                            ) : filteredLedgerEntries.length === 0 ? (
                                <div className="text-center py-20">
                                    <Receipt className="text-slate-300 mx-auto mb-4" sx={{ fontSize: 56 }} />
                                    <p className="text-slate-500">No transactions found</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-slate-100">
                                    {filteredLedgerEntries.map((entry) => {
                                        const statusStyles = getStatusStyles(entry.paymentStatus);
                                        return (
                                            <div key={entry.id} className="px-4 sm:px-6 py-3 hover:bg-slate-50/50 transition-colors">
                                                <div className="flex items-center justify-between gap-4">
                                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                                        <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                                                            {getTypeIcon(entry.type)}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-0.5">
                                                                <p className="font-semibold text-slate-800 text-sm truncate">{entry.customerName || 'N/A'}</p>
                                                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${statusStyles.badge}`}>
                                                                    {statusStyles.label}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-xs text-slate-500">
                                                                <span className="truncate">{entry.subscriptionName || entry.type}</span>
                                                                <span>• {formatDate(entry.createdAt)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0">
                                                        <p className={`text-base font-bold ${statusStyles.text}`}>
                                                            {entry.paymentStatus === 'PAID' ? '+ ' : ''}{formatCurrency(entry.amount)}
                                                        </p>
                                                        {entry.transferAmount && entry.transferAmount !== entry.amount && entry.paymentStatus === 'PAID' && (
                                                            <p className="text-xs text-slate-400">Net: ₹{entry.transferAmount.toFixed(2)}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            
                            {/* Load More */}
                            {hasMoreEntries && (
                                <div className="p-4 border-t border-slate-100 bg-slate-50">
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => loadLedgerEntries(selectedAccountForTrends.account.approverSPID, ledgerYear, ledgerMonth, false)}
                                        disabled={loadingMore}
                                        startIcon={loadingMore ? <CircularProgress size={16} /> : <ExpandMore />}
                                        sx={{ textTransform: 'none', borderRadius: '10px' }}
                                    >
                                        {loadingMore ? 'Loading...' : 'Load More'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Stat Component
function Stat({ icon, label, value, color, className = '' }: { 
    icon: React.ReactNode; 
    label: string; 
    value: number; 
    color: string;
    className?: string;
}) {
    const colorMap: Record<string, { bg: string; text: string; icon: string }> = {
        emerald: { bg: 'from-emerald-50 to-teal-50', text: 'text-emerald-700', icon: 'text-emerald-500' },
        amber: { bg: 'from-amber-50 to-orange-50', text: 'text-amber-700', icon: 'text-amber-500' },
        violet: { bg: 'from-violet-50 to-indigo-50', text: 'text-violet-700', icon: 'text-violet-500' },
        slate: { bg: 'from-slate-50 to-gray-100', text: 'text-slate-700', icon: 'text-slate-500' },
        blue: { bg: 'from-blue-50 to-cyan-50', text: 'text-blue-700', icon: 'text-blue-500' },
        purple: { bg: 'from-purple-50 to-fuchsia-50', text: 'text-purple-700', icon: 'text-purple-500' },
    };
    
    const colors = colorMap[color] || colorMap.slate;
    
    return (
        <div className={`bg-gradient-to-br ${colors.bg} rounded-xl p-3.5 border border-slate-100 shadow-sm ${className}`}>
            <div className="flex items-center gap-2 mb-1.5">
                <span className={`${colors.icon}`} style={{ fontSize: 18 }}>{icon}</span>
                <span className="text-xs text-slate-500 uppercase tracking-wide font-medium">{label}</span>
            </div>
            <p className={`text-lg sm:text-xl font-bold ${colors.text}`}>₹{formatCompact(value)}</p>
        </div>
    );
}

// Mobile Metric Pill
function MetricPill({ label, value, color }: { label: string; value: number; color: string }) {
    const colorMap: Record<string, string> = {
        emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700',
        violet: 'bg-violet-50 text-violet-700',
        blue: 'bg-blue-50 text-blue-700',
        purple: 'bg-purple-50 text-purple-700',
        slate: 'bg-slate-100 text-slate-600',
    };
    
    return (
        <div className={`${colorMap[color] || colorMap.slate} rounded-lg px-2.5 py-2 text-center`}>
            <p className="text-[10px] text-slate-500 mb-0.5">{label}</p>
            <p className="font-semibold text-sm">₹{formatCompact(value)}</p>
        </div>
    );
}
